import { type HttpClient, query, segment } from '../http';

import {
  type AddWishlistPayload,
  type ConvertWishlistPayload,
  type CreateWishlistListPayload,
  type ListWishlistQuery,
  type ListWishlistResult,
  type WishlistItem,
  type WishlistList,
  type WishlistListsResult,
} from './wishlists.types';

/**
 * What the reader wants, and what the household wants.
 *
 * @param http Transport supplied by the application.
 * @returns The `wishlists` domain of the API client.
 */
export function createWishlistsApi(http: HttpClient) {
  return {
    /**
     * Lists wanted games: the reader's own, or every member's together.
     *
     * @param params Scope, search, sort and pagination.
     * @returns One page of rows with the total.
     * @throws `wishlist.error.list.not_in_family` (400) — `scope: 'family'`
     *   without a household.
     */
    list: (params: ListWishlistQuery = {}) =>
      http.get<ListWishlistResult>(
        `/wishlists${query({
          scope: params.scope,
          listId: params.listId,
          search: params.search,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          page: params.page,
          limit: params.limit,
        })}`,
      ),

    /**
     * Wants a game. It lands at the end of the reader's own order.
     *
     * @param payload The game, and why you want it.
     * @returns The row that was created.
     * @throws 404 when no game has that id, 409 when it is already wanted.
     */
    add: (payload: AddWishlistPayload) =>
      http.post<WishlistItem>('/wishlists', payload),

    /**
     * The reader's own lists, with how many games each holds.
     *
     * @returns Every list, default first, plus the household's own count.
     */
    listLists: () => http.get<WishlistListsResult>('/wishlists/lists'),

    /**
     * Opens a new named list.
     *
     * @param payload The list's name.
     * @returns The created list, empty.
     * @throws 409 when a list of that name already exists.
     */
    createList: (payload: CreateWishlistListPayload) =>
      http.post<WishlistList>('/wishlists/lists', payload),

    /**
     * Deletes a list and everything in it.
     *
     * @param id The list — **not** an item.
     * @returns Nothing.
     * @throws 403 when the list is somebody else's, 404 when it is gone, 409
     *   on the default list, which nobody can be left without.
     */
    removeList: (id: string) =>
      http.delete<void>(`/wishlists/lists/${segment(id)}`),

    /**
     * What the household wants, minus the reader's own rows — the gift board.
     *
     * @returns Somebody else's wanted games, each with who has claimed it.
     * @throws `wishlist.error.list.not_in_family` (400) — no household.
     */
    listGifts: () => http.get<readonly WishlistItem[]>('/wishlists/gifts'),

    /**
     * Claims somebody else's wanted game, so the family does not buy it twice.
     *
     * Warning: the person who wants it is **never** told. That is the whole
     * point, and it is enforced on the server, not here.
     *
     * @param id The wishlist item to claim.
     * @returns Nothing.
     * @throws 403 outside the household, 404 when it is gone, 409 on your own
     *   row or on one somebody else claimed first.
     */
    reserveGift: (id: string) =>
      http.post<void>(`/wishlists/${segment(id)}/reserve`, {}),

    /**
     * Gives back a gift the reader had claimed.
     *
     * @param id The wishlist item.
     * @returns Nothing.
     * @throws 403 when somebody else claimed it, 404 when it is gone.
     */
    releaseGift: (id: string) =>
      http.delete<void>(`/wishlists/${segment(id)}/reserve`),

    /**
     * Rewrites the reader's whole order.
     *
     * Warning: Send the **entire** list of item ids in their new order, not
     * the one row that moved — the server rejects a list that does not match
     * the wishlist exactly, which is what stops a stale screen from silently
     * dropping a row.
     *
     * @param itemIds Every item id, in the wanted order.
     * @returns Nothing.
     * @throws 400 when the list does not match the wishlist.
     */
    reorder: (itemIds: readonly string[], listId?: string) =>
      http.patch<void>('/wishlists/reorder', { orderedIds: itemIds, listId }),

    /**
     * Turns a wanted game into an owned one: it enters the library and leaves
     * the wishlist, in one step.
     *
     * @param id The wishlist item.
     * @param payload How it was bought, if that is worth recording.
     * @returns Nothing.
     * @throws 403 when the row is somebody else's, 404 when it is gone, 409
     *   when the game is already owned.
     */
    convertToLibrary: (id: string, payload: ConvertWishlistPayload = {}) =>
      http.post<void>(`/wishlists/${segment(id)}/convert-library`, payload),

    /**
     * Stops wanting a game.
     *
     * @param id The wishlist item — **not** the game.
     * @returns Nothing.
     * @throws 403 when the row is somebody else's, 404 when it is gone.
     */
    remove: (id: string) => http.delete<void>(`/wishlists/${segment(id)}`),
  };
}

export type WishlistsApi = ReturnType<typeof createWishlistsApi>;
